import { Box, Card, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { MOBEAT_VIS as C } from './landingVisualTheme';

// Code-rendered, dark mock of a Mobeat revenue panel (matches the real product's look). Hero visual;
// all numbers are dummy. The stacked area mirrors the Revenue Chart: New (blue) + Renewal (teal), with
// a dashed forecast tail.
const KPIS = [
  { label: 'MRR', value: '$128.4k', delta: '+12.4%', color: C.teal },
  { label: 'Active subscribers', value: '46,369', delta: '+3.1%', color: C.blue },
  { label: 'Churn', value: '3.1%', delta: '-0.6%', color: C.green },
  { label: 'LTV / payer', value: '$42.80', delta: '+8.9%', color: C.gold },
];

const NEW = [36, 34, 33, 28, 21, 14, 9, 5];
const RENEWAL = [18, 22, 30, 31, 26, 19, 13, 8];
const SPLIT = 4; // index where forecast (dashed) begins

const W = 560;
const H = 200;
const PAD = 14;

function scale(series) {
  const totals = NEW.map((n, i) => n + RENEWAL[i]);
  const max = Math.max(...totals) * 1.12;
  const stepX = (W - PAD * 2) / (series.length - 1);
  return series.map((v, i) => [PAD + i * stepX, H - PAD - (v / max) * (H - PAD * 2)]);
}

const linePath = (pts, a, b) =>
  pts
    .slice(a, b)
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');

const areaPath = (top, bottom) => {
  const up = top.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const down = [...bottom].reverse().map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return `${up} ${down} Z`;
};

export default function LandingDashboardMock({ sx }) {
  const newTop = scale(NEW);
  const totalTop = scale(NEW.map((n, i) => n + RENEWAL[i]));
  const base = newTop.map(([x]) => [x, H - PAD]);

  return (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        width: '100%',
        borderRadius: 3,
        bgcolor: C.bg,
        border: `1px solid ${C.border}`,
        boxShadow: '0 30px 70px -24px rgba(0,0,0,0.55)',
        ...sx,
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <Stack direction='row' spacing={0.75}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
          ))}
        </Stack>
        <Typography variant='caption' sx={{ color: C.dim, ml: 1, fontWeight: 600 }}>
          Mobeat · Revenue
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ px: 1.25, py: 0.25, borderRadius: 1, fontSize: 11, color: C.dim, border: `1px solid ${C.border}` }}>
          Last 90 days
        </Box>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25, mb: 2 }}>
        {KPIS.map((k) => (
          <Box key={k.label} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: C.tile, borderLeft: `3px solid ${k.color}` }}>
            <Typography sx={{ fontSize: 10.5, color: C.dim, mb: 0.25 }} noWrap>
              {k.label}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, color: C.text }} noWrap>
              {k.value}
            </Typography>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: C.green }}>{k.delta}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ borderRadius: 1.5, p: 1, bgcolor: C.panel, border: `1px solid ${C.border}` }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ px: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: C.dim }}>Revenue</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {[
            ['New', C.blue],
            ['Renewal', C.teal],
          ].map(([label, color]) => (
            <Stack key={label} direction='row' spacing={0.5} alignItems='center'>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
              <Typography sx={{ fontSize: 10, color: C.dim }}>{label}</Typography>
            </Stack>
          ))}
        </Stack>
        <Box component='svg' viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', display: 'block' }}>
          <defs>
            <linearGradient id='mbNew' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={C.blue} stopOpacity={0.55} />
              <stop offset='100%' stopColor={C.blue} stopOpacity={0.08} />
            </linearGradient>
            <linearGradient id='mbRen' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={C.teal} stopOpacity={0.5} />
              <stop offset='100%' stopColor={C.teal} stopOpacity={0.08} />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1={PAD} x2={W - PAD} y1={PAD + f * (H - PAD * 2)} y2={PAD + f * (H - PAD * 2)} stroke={C.grid} />
          ))}
          {/* renewal band (between new-top and total-top) then new band */}
          <path d={areaPath(totalTop, newTop)} fill='url(#mbRen)' />
          <path d={areaPath(newTop, base)} fill='url(#mbNew)' />
          {/* actual (solid) vs forecast (dashed) boundary lines */}
          <path d={linePath(totalTop, 0, SPLIT)} fill='none' stroke={C.teal} strokeWidth={2.5} />
          <path d={linePath(totalTop, SPLIT - 1, totalTop.length)} fill='none' stroke={C.teal} strokeWidth={2.5} strokeDasharray='5 5' opacity={0.8} />
          <path d={linePath(newTop, 0, SPLIT)} fill='none' stroke={C.blue} strokeWidth={2.5} />
          <path d={linePath(newTop, SPLIT - 1, newTop.length)} fill='none' stroke={C.blue} strokeWidth={2.5} strokeDasharray='5 5' opacity={0.8} />
          <line x1={totalTop[SPLIT - 1][0]} x2={totalTop[SPLIT - 1][0]} y1={PAD} y2={H - PAD} stroke={C.dim} strokeDasharray='3 4' opacity={0.5} />
        </Box>
      </Box>
    </Card>
  );
}

LandingDashboardMock.propTypes = {
  sx: PropTypes.object,
};
